import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Wallet, RefreshCw, TrendingUp,
    Percent, Calendar, AlertCircle, Clock,
    CheckCircle, CreditCard, XCircle, Users,
    Loader2
} from 'lucide-react';
import DataGrid from '../../components/datagrid/DataGrid';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS, FLEET_HUB_METHODS } from '../../config/environment';
import { formatTL } from '../../utils/money';
import TlIcon from '../../components/TlIcon';
import './EarningsPage.css';

const COMMISSION_STATUS = {
    0: { label: 'Beklemede', tone: 'pending' },
    1: { label: 'Onaylandı', tone: 'approved' },
    2: { label: 'Faturalandı', tone: 'invoiced' },
    3: { label: 'Ödendi', tone: 'paid' },
    4: { label: 'İptal', tone: 'cancelled' },
    5: { label: 'İade', tone: 'refunded' }
};

const PAYMENT_STATUS = {
    0: { label: 'Fatura Bekleniyor', tone: 'waiting' },
    1: { label: 'Onay Bekliyor', tone: 'pending' },
    2: { label: 'Onaylandı', tone: 'approved' },
    3: { label: 'Ödendi', tone: 'paid' },
    4: { label: 'Reddedildi', tone: 'rejected' }
};

const VIEW_TABS = [
    { key: 'instant', label: 'Anlık Kazançlar', icon: TrendingUp, color: '#22c55e' },
    { key: 'couriers', label: 'Kurye Kazançları', icon: Users, color: '#f59e0b' },
    { key: 'payments', label: 'Haftalık Hakedişler', icon: Wallet, color: '#6366f1' }
];

const EarningsPage = () => {
    const [activeView, setActiveView] = useState('instant');

    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(true);

    const [earnings, setEarnings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalRecords, setTotalRecords] = useState(0);

    const [sortField, setSortField] = useState('CalculatedAt');
    const [sortOrder, setSortOrder] = useState('desc');

    const [filters, setFilters] = useState({});
    const [filterTypes, setFilterTypes] = useState({});

    const [payments, setPayments] = useState([]);
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [paymentsError, setPaymentsError] = useState('');
    const [paymentsPage, setPaymentsPage] = useState(1);
    const [paymentsPageSize, setPaymentsPageSize] = useState(25);
    const [paymentsTotalRecords, setPaymentsTotalRecords] = useState(0);
    const [paymentsSortField, setPaymentsSortField] = useState('CreatedAtUtc');
    const [paymentsSortOrder, setPaymentsSortOrder] = useState('desc');
    const [paymentsFilters, setPaymentsFilters] = useState({});
    const [paymentsFilterTypes, setPaymentsFilterTypes] = useState({});

    const [courierEarnings, setCourierEarnings] = useState([]);
    const [courierEarningsLoading, setCourierEarningsLoading] = useState(false);

    const formatCurrency = (amount) => {
        if (amount == null) return '0 TL';
        return formatTL(amount);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleString('tr-TR', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const formatDateShort = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('tr-TR', {
            year: 'numeric', month: '2-digit', day: '2-digit'
        });
    };

    const formatPercent = (val) => {
        if (val == null) return '-';
        return `%${Number(val).toFixed(1)}`;
    };

    const fetchSummary = useCallback(async () => {
        setSummaryLoading(true);
        try {
            const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.EARNINGS_SUMMARY);
            if (res.success) {
                setSummary(res.data);
            }
        } catch (err) {
            console.error('Summary error:', err);
        } finally {
            setSummaryLoading(false);
        }
    }, []);

    const fetchCourierEarnings = useCallback(async () => {
        setCourierEarningsLoading(true);
        try {
            const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.COURIER_EARNINGS);
            if (res.success) {
                setCourierEarnings(res.data || []);
            }
        } catch (err) {
            console.error('Courier earnings error:', err);
        } finally {
            setCourierEarningsLoading(false);
        }
    }, []);

    const buildFilterModel = useCallback(() => {
        const filterModel = {};
        Object.keys(filters).forEach((key) => {
            if (filters[key] !== undefined && filters[key] !== null && String(filters[key]).trim() !== '') {
                filterModel[key] = {
                    filter: filters[key],
                    type: filterTypes[key] || 'contains',
                    caseSensitive: false
                };
            }
        });
        return Object.keys(filterModel).length > 0 ? filterModel : null;
    }, [filters, filterTypes]);

    const buildPaymentsFilterModel = useCallback(() => {
        const filterModel = {};
        Object.keys(paymentsFilters).forEach((key) => {
            if (paymentsFilters[key] !== undefined && paymentsFilters[key] !== null && String(paymentsFilters[key]).trim() !== '') {
                filterModel[key] = {
                    filter: paymentsFilters[key],
                    type: paymentsFilterTypes[key] || 'contains',
                    caseSensitive: false
                };
            }
        });
        return Object.keys(filterModel).length > 0 ? filterModel : null;
    }, [paymentsFilters, paymentsFilterTypes]);

    const fetchEarnings = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const requestBody = {
                page: currentPage,
                pageSize,
                sortModels: sortField ? [{ colId: sortField, sort: sortOrder }] : null,
                filterModel: buildFilterModel()
            };

            const res = await apiService.post(API_ENDPOINTS.FLEET_PANEL.EARNINGS_LIST, requestBody);
            if (res.success && res.data) {
                const data = res.data.data || res.data;
                setEarnings(data.items || []);
                setTotalRecords(data.totalCount || 0);
            } else {
                setError(res.error || 'Veriler yüklenemedi');
            }
        } catch (err) {
            setError('Bir hata oluştu');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, sortField, sortOrder, buildFilterModel]);

    const fetchPayments = useCallback(async () => {
        setPaymentsLoading(true);
        setPaymentsError('');
        try {
            const requestBody = {
                page: paymentsPage,
                pageSize: paymentsPageSize,
                sortModels: paymentsSortField ? [{ colId: paymentsSortField, sort: paymentsSortOrder }] : null,
                filterModel: buildPaymentsFilterModel()
            };

            const res = await apiService.post(API_ENDPOINTS.FLEET_PANEL.FLEET_PAYMENTS_LIST, requestBody);
            if (res.success && res.data) {
                const data = res.data.data || res.data;
                setPayments(data.items || []);
                setPaymentsTotalRecords(data.totalCount || 0);
            } else {
                setPaymentsError(res.error || 'Veriler yüklenemedi');
            }
        } catch (err) {
            setPaymentsError('Bir hata oluştu');
        } finally {
            setPaymentsLoading(false);
        }
    }, [paymentsPage, paymentsPageSize, paymentsSortField, paymentsSortOrder, buildPaymentsFilterModel]);

    useEffect(() => { fetchSummary(); }, [fetchSummary]);
    useEffect(() => { if (activeView === 'instant') fetchEarnings(); }, [fetchEarnings, activeView]);
    useEffect(() => { if (activeView === 'payments') fetchPayments(); }, [fetchPayments, activeView]);
    useEffect(() => { if (activeView === 'couriers') fetchCourierEarnings(); }, [fetchCourierEarnings, activeView]);

    useEffect(() => {
        const handler = () => {
            fetchSummary();
            if (activeView === 'instant') fetchEarnings();
            if (activeView === 'couriers') fetchCourierEarnings();
        };
        window.addEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_DELIVERY_UPDATE_NOTIFICATION}`, handler);
        return () => {
            window.removeEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_DELIVERY_UPDATE_NOTIFICATION}`, handler);
        };
    }, [fetchSummary, fetchEarnings, fetchCourierEarnings, activeView]);

    const handleSort = (field, order) => { setSortField(field); setSortOrder(order); };
    const handleFilterChange = (field, value, type) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setFilterTypes(prev => ({ ...prev, [field]: type }));
        setCurrentPage(1);
    };
    const handlePageSizeChange = (size) => { setPageSize(size); setCurrentPage(1); };

    const handlePaymentsSort = (field, order) => { setPaymentsSortField(field); setPaymentsSortOrder(order); };
    const handlePaymentsFilterChange = (field, value, type) => {
        setPaymentsFilters(prev => ({ ...prev, [field]: value }));
        setPaymentsFilterTypes(prev => ({ ...prev, [field]: type }));
        setPaymentsPage(1);
    };
    const handlePaymentsPageSizeChange = (size) => { setPaymentsPageSize(size); setPaymentsPage(1); };

    const handleViewChange = (view) => {
        if (view !== activeView) {
            setActiveView(view);
        }
    };

    const handleRefresh = () => {
        fetchSummary();
        if (activeView === 'instant') fetchEarnings();
        else if (activeView === 'couriers') fetchCourierEarnings();
        else fetchPayments();
    };

    const s = summary || {};

    const statCards = [
        { title: 'Toplam Kazanc', value: formatCurrency(s.totalFleetEarnings), icon: TlIcon, color: 'green', subtitle: `${s.totalCommissions ?? 0} komisyon` },
        { title: 'Bu Hafta', value: formatCurrency(s.thisWeekEarnings), icon: TrendingUp, color: 'blue', subtitle: `${s.thisWeekCommissions ?? 0} komisyon` },
        { title: 'Bu Ay', value: formatCurrency(s.thisMonthEarnings), icon: Calendar, color: 'purple', subtitle: 'Aylik toplam' },
        {
            title: 'Ort. Kazanc', value: formatCurrency(s.averageCommissionAmount), icon: Percent, color: 'orange',
            subtitle: s.averageFleetFeePerPackage > 0
                ? `Ort. paket: ${formatCurrency(s.averageFleetFeePerPackage)}`
                : s.averageFleetShareRate > 0
                    ? `Ort. oran: ${formatPercent(s.averageFleetShareRate)}`
                    : `${s.totalCommissions ?? 0} komisyon`
        }
    ];

    const earningsColumns = useMemo(() => [
        { field: 'calculatedAt', header: 'Tarih', sortable: true, render: (row) => formatDate(row.calculatedAt) },
        { field: 'partnerName', header: 'Partner', sortable: true, filterable: true, render: (row) => <span className="fe-partner-name">{row.partnerName || '-'}</span> },
        { field: 'courierName', header: 'Kurye', sortable: true, filterable: true, render: (row) => <span>{row.courierName || '-'}</span> },
        {
            field: 'stackingIndex', header: 'Kademe', sortable: false,
            render: (row) => row.stackingIndex
                ? <span className="fe-stacking-badge">{row.stackingIndex}. paket</span>
                : '-'
        },
        { field: 'courierEarning', header: 'Kurye Maliyeti', sortable: true, render: (row) => {
            if (row.stackingIndex && row.stackingIndex > 1) {
                return <span className="fe-discounted-fee">{formatCurrency(row.courierEarning)}</span>;
            }
            return formatCurrency(row.courierEarning);
        }},
        { field: 'fleetShareAmount', header: 'Filo Kazancı', sortable: true, render: (row) => <span className="fe-amount-highlight">{formatCurrency(row.fleetShareAmount)}</span> },
        {
            field: 'status', header: 'Durum', sortable: true,
            render: (row) => {
                const st = COMMISSION_STATUS[row.status] || { label: 'Bilinmiyor', tone: 'unknown' };
                return <span className={`fe-status-badge ${st.tone}`}>{st.label}</span>;
            }
        }
    ], []);

    const courierEarningsColumns = useMemo(() => [
        {
            field: 'courierName', header: 'Kurye', sortable: false,
            render: (row) => (
                <div className="fe-courier-cell">
                    <span className="fe-courier-name">{row.courierName}</span>
                    <span className="fe-courier-phone">{row.courierPhone || ''}</span>
                </div>
            )
        },
        {
            field: 'status', header: 'Durum', sortable: false,
            render: (row) => (
                <div className="fe-courier-status-cell">
                    <span className={`fe-status-dot ${row.isActive ? (row.isBusy ? 'busy' : 'active') : 'offline'}`} />
                    <span>{row.isActive ? (row.isBusy ? 'Mesgul' : 'Aktif') : 'Pasif'}</span>
                </div>
            )
        },
        { field: 'todayEarnings', header: 'Bugun', sortable: false, render: (row) => <span className="fe-amount-highlight">{formatCurrency(row.todayEarnings)}</span> },
        { field: 'todayDeliveries', header: 'Bugun Teslimat', sortable: false, render: (row) => row.todayDeliveries },
        { field: 'thisWeekEarnings', header: 'Bu Hafta', sortable: false, render: (row) => formatCurrency(row.thisWeekEarnings) },
        { field: 'thisMonthEarnings', header: 'Bu Ay', sortable: false, render: (row) => formatCurrency(row.thisMonthEarnings) },
        { field: 'totalEarnings', header: 'Toplam', sortable: false, render: (row) => <strong>{formatCurrency(row.totalEarnings)}</strong> },
        { field: 'totalDeliveries', header: 'Top. Teslimat', sortable: false, render: (row) => row.totalDeliveries },
        { field: 'lastDeliveryAt', header: 'Son Teslimat', sortable: false, render: (row) => row.lastDeliveryAt ? formatDate(row.lastDeliveryAt) : '-' }
    ], []);

    const paymentsColumns = useMemo(() => [
        {
            field: 'period', header: 'Donem', sortable: true,
            render: (row) => (
                <div className="fe-period-cell">
                    <span className="fe-period-text">{row.period || '-'}</span>
                    {row.periodRange && <span className="fe-period-range">{row.periodRange}</span>}
                </div>
            )
        },
        { field: 'totalDeliveries', header: 'Teslimat', sortable: true, render: (row) => <span className="fe-delivery-badge">{row.totalDeliveries || 0}</span> },
        { field: 'totalFleetShareAmount', header: 'Filo Payi', sortable: true, render: (row) => <span className="fe-amount-highlight">{formatCurrency(row.totalFleetShareAmount)}</span> },
        {
            field: 'status', header: 'Durum', sortable: true,
            render: (row) => {
                const st = PAYMENT_STATUS[row.status] || { label: 'Bilinmiyor', tone: 'unknown' };
                return <span className={`fe-status-badge ${st.tone}`}>{st.label}</span>;
            }
        },
        { field: 'createdAtUtc', header: 'Olusturulma', sortable: true, render: (row) => formatDateShort(row.createdAtUtc) }
    ], []);

    const renderEarningCard = (item) => {
        const st = COMMISSION_STATUS[item.status] || { label: 'Bilinmiyor', tone: 'unknown' };
        return (
            <div className="fe-earning-card">
                <div className="fe-card-header">
                    <div>
                        <h3>{item.partnerName || 'Partner'}</h3>
                        <p>{formatDate(item.calculatedAt)}</p>
                    </div>
                    <span className={`fe-status-badge ${st.tone}`}>{st.label}</span>
                </div>
                <div className="fe-card-stats">
                    <div className="fe-card-stat">
                        <span className="fe-card-stat-label">Kurye</span>
                        <span className="fe-card-stat-value">{item.courierName || '-'}</span>
                    </div>
                    <div className="fe-card-stat">
                        <span className="fe-card-stat-label">Kurye Maliyeti</span>
                        <span className="fe-card-stat-value">{formatCurrency(item.courierEarning)}</span>
                    </div>
                    <div className="fe-card-stat highlight">
                        <span className="fe-card-stat-label">Filo Kazanci</span>
                        <span className="fe-card-stat-value">{formatCurrency(item.fleetShareAmount)}</span>
                    </div>
                </div>
            </div>
        );
    };

    const renderCourierEarningCard = (item) => (
        <div className="fe-earning-card">
            <div className="fe-card-header">
                <div>
                    <h3>{item.courierName}</h3>
                    <p>{item.courierPhone || ''}</p>
                </div>
                <span className={`fe-status-dot-lg ${item.isActive ? (item.isBusy ? 'busy' : 'active') : 'offline'}`}>
                    {item.isActive ? (item.isBusy ? 'Mesgul' : 'Aktif') : 'Pasif'}
                </span>
            </div>
            <div className="fe-card-stats">
                <div className="fe-card-stat highlight">
                    <span className="fe-card-stat-label">Bugun</span>
                    <span className="fe-card-stat-value">{formatCurrency(item.todayEarnings)}</span>
                </div>
                <div className="fe-card-stat">
                    <span className="fe-card-stat-label">Bu Hafta</span>
                    <span className="fe-card-stat-value">{formatCurrency(item.thisWeekEarnings)}</span>
                </div>
                <div className="fe-card-stat">
                    <span className="fe-card-stat-label">Toplam</span>
                    <span className="fe-card-stat-value">{formatCurrency(item.totalEarnings)}</span>
                </div>
            </div>
            <div className="fe-card-footer">
                <span>Bugun: {item.todayDeliveries} teslimat | Toplam: {item.totalDeliveries} teslimat</span>
            </div>
        </div>
    );

    const renderPaymentCard = (item) => {
        const st = PAYMENT_STATUS[item.status] || { label: 'Bilinmiyor', tone: 'unknown' };
        return (
            <div className="fe-earning-card">
                <div className="fe-card-header">
                    <div>
                        <h3>{item.period || 'Donem'}</h3>
                        <p>{item.periodRange}</p>
                    </div>
                    <span className={`fe-status-badge ${st.tone}`}>{st.label}</span>
                </div>
                <div className="fe-card-stats">
                    <div className="fe-card-stat">
                        <span className="fe-card-stat-label">Teslimat</span>
                        <span className="fe-card-stat-value">{item.totalDeliveries || 0}</span>
                    </div>
                    <div className="fe-card-stat highlight">
                        <span className="fe-card-stat-label">Filo Payi</span>
                        <span className="fe-card-stat-value">{formatCurrency(item.totalFleetShareAmount)}</span>
                    </div>
                    <div className="fe-card-stat">
                        <span className="fe-card-stat-label">Donem</span>
                        <span className="fe-card-stat-value">{item.periodRange || '-'}</span>
                    </div>
                </div>
            </div>
        );
    };

    if (summaryLoading && !summary) {
        return (
            <div className="fe-container">
                <div className="page-loading">
                    <Loader2 className="spinning" size={40} />
                    <span>Yukleniyor...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="fe-container">
            <div className="page-header">
                <div className="header-left">
                    <div className="page-icon"><Wallet size={20} /></div>
                    <div className="page-title">
                        <h1>Gelir Paylasimi</h1>
                        <p>Filo gelir paylasimi detaylari ve hakedis listesi</p>
                    </div>
                </div>
                <button className="btn btn-secondary" onClick={handleRefresh}>
                    <RefreshCw size={16} />
                    Yenile
                </button>
            </div>

            {(error || paymentsError) && (
                <div className="error-banner">
                    <AlertCircle size={18} />
                    <span>{error || paymentsError}</span>
                </div>
            )}

            <div className="fe-stats-grid">
                {statCards.map((card, index) => {
                    const Icon = card.icon;
                    return (
                        <div key={index} className="fe-stat-card">
                            <div className="fe-stat-icon"><Icon size={20} /></div>
                            <div className="fe-stat-content">
                                <span className="fe-stat-label">{card.title}</span>
                                <span className="fe-stat-value">{card.value}</span>
                                <span className="fe-stat-sub">{card.subtitle}</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="fe-view-tabs">
                {VIEW_TABS.map(tab => {
                    const Icon = tab.icon;
                    const isActive = activeView === tab.key;
                    const count = tab.key === 'instant' ? totalRecords
                        : tab.key === 'couriers' ? courierEarnings.length
                        : paymentsTotalRecords;
                    return (
                        <button
                            key={tab.key}
                            className={`fe-view-tab ${isActive ? 'active' : ''}`}
                            onClick={() => handleViewChange(tab.key)}
                            style={{ '--vtab-c': tab.color }}
                        >
                            <Icon size={16} />
                            <span>{tab.label}</span>
                            <span className="fe-view-tab-count">{count}</span>
                        </button>
                    );
                })}
            </div>

            {activeView === 'instant' && (
                <div className="fe-grid-section">
                    <div className="fe-grid-header">
                        <h2><TrendingUp size={20} /> Anlik Kazanclar</h2>
                        <span className="fe-grid-count">{totalRecords} kayit</span>
                    </div>
                    <DataGrid
                        data={earnings}
                        columns={earningsColumns}
                        loading={loading}
                        error={error}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        totalRecords={totalRecords}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={handlePageSizeChange}
                        sortField={sortField}
                        sortOrder={sortOrder}
                        onSort={handleSort}
                        filters={filters}
                        filterTypes={filterTypes}
                        onFilterChange={handleFilterChange}
                        renderCard={renderEarningCard}
                        emptyIcon={TrendingUp}
                        emptyMessage="Henuz komisyon kaydi bulunmuyor"
                        loadingMessage="Yukleniyor..."
                    />
                </div>
            )}

            {activeView === 'couriers' && (
                <div className="fe-grid-section">
                    <div className="fe-grid-header">
                        <h2><Users size={20} /> Kurye Kazanclari</h2>
                        <span className="fe-grid-count">{courierEarnings.length} kurye</span>
                    </div>
                    <DataGrid
                        data={courierEarnings}
                        columns={courierEarningsColumns}
                        loading={courierEarningsLoading}
                        currentPage={1}
                        pageSize={100}
                        totalRecords={courierEarnings.length}
                        onPageChange={() => {}}
                        onPageSizeChange={() => {}}
                        renderCard={renderCourierEarningCard}
                        emptyIcon={Users}
                        emptyMessage="Henuz kurye kazanc verisi bulunmuyor"
                        loadingMessage="Yukleniyor..."
                    />
                </div>
            )}

            {activeView === 'payments' && (
                <div className="fe-grid-section">
                    <div className="fe-grid-header">
                        <h2><Wallet size={20} /> Haftalik Hakedisler</h2>
                        <span className="fe-grid-count">{paymentsTotalRecords} kayit</span>
                    </div>
                    <DataGrid
                        data={payments}
                        columns={paymentsColumns}
                        loading={paymentsLoading}
                        error={paymentsError}
                        currentPage={paymentsPage}
                        pageSize={paymentsPageSize}
                        totalRecords={paymentsTotalRecords}
                        onPageChange={setPaymentsPage}
                        onPageSizeChange={handlePaymentsPageSizeChange}
                        sortField={paymentsSortField}
                        sortOrder={paymentsSortOrder}
                        onSort={handlePaymentsSort}
                        filters={paymentsFilters}
                        filterTypes={paymentsFilterTypes}
                        onFilterChange={handlePaymentsFilterChange}
                        renderCard={renderPaymentCard}
                        emptyIcon={Wallet}
                        emptyMessage="Henuz hakedis kaydi bulunmuyor"
                        loadingMessage="Yukleniyor..."
                    />
                </div>
            )}
        </div>
    );
};

export default EarningsPage;
